import { useEffect, useMemo, useState } from 'react';
import type { AdminUserRow } from '../../../domain/superadmin/types';
import { buildDeleteChallenge } from '../../../domain/superadmin/userDelete';
import { Button } from '../../../components/ui';
import Input from '../../../components/ui/Input';
import Modal from '../../../components/ui/Modal';

interface DeleteUsersDialogProps {
    open: boolean;
    users: AdminUserRow[];
    onClose: () => void;
    onConfirm: (ids: string[]) => Promise<void>;
}

export function DeleteUsersDialog({ open, users, onClose, onConfirm }: DeleteUsersDialogProps) {
    const [step, setStep] = useState<1 | 2>(1);
    const [challengeInput, setChallengeInput] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const emails = useMemo(() => users.map((u) => u.email), [users]);
    const challenge = useMemo(() => buildDeleteChallenge(emails), [emails]);
    const hasOwnedOrgs = users.some((u) => u.organizationCount > 0);
    const match = challengeInput.trim() === challenge;

    useEffect(() => {
        if (!open) {
            setStep(1);
            setChallengeInput('');
            setIsDeleting(false);
        }
    }, [open]);

    const handleConfirm = async () => {
        if (!match) return;
        setIsDeleting(true);
        try {
            await onConfirm(users.map((u) => u.id));
        } finally {
            setIsDeleting(false);
        }
    };

    const title = users.length === 1 ? 'Excluir usuário' : `Excluir ${users.length} usuários`;

    return (
        <Modal isOpen={open} onClose={onClose} size="md" preventBackdropClose={isDeleting}>
            <Modal.Header onClose={onClose} showCloseButton={!isDeleting}>
                {title}
            </Modal.Header>

            <Modal.Body>
                {step === 1 ? (
                    <div className="flex flex-col gap-s4">
                        <p className="text-sm text-secondary">
                            Esta ação é permanente e não pode ser desfeita. Os seguintes usuários serão excluídos:
                        </p>
                        <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
                            {users.map((u) => (
                                <li key={u.id} className="text-default">
                                    <span className="font-medium">{u.fullName ?? 'Sem nome'}</span>
                                    <span className="text-muted"> — {u.email}</span>
                                </li>
                            ))}
                        </ul>
                        {hasOwnedOrgs && (
                            <p className="text-sm text-danger-text" role="alert">
                                Isto apagará organização(ões) e dados associados, e poderá remover outros usuários
                                dessas organizações.
                            </p>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col gap-s4">
                        <p className="text-sm text-secondary">
                            Para confirmar, digite exatamente:{' '}
                            <span className="font-mono font-medium text-default">{challenge}</span>
                        </p>
                        <Input
                            label="Confirmação"
                            value={challengeInput}
                            onChange={(e) => setChallengeInput(e.target.value)}
                            placeholder={challenge}
                            autoComplete="off"
                            disabled={isDeleting}
                        />
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <Button type="button" variant="ghost" onClick={onClose} disabled={isDeleting}>
                    Cancelar
                </Button>
                {step === 1 ? (
                    <Button type="button" variant="danger" onClick={() => setStep(2)}>
                        Continuar
                    </Button>
                ) : (
                    <Button
                        type="button"
                        variant="danger"
                        onClick={() => void handleConfirm()}
                        disabled={!match || isDeleting}
                    >
                        {isDeleting ? 'Excluindo...' : 'Excluir'}
                    </Button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
